import React, { useState, useEffect } from 'react';
import './KYCNotifications.css';

const KYCNotifications = ({ merchantId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    // Set up polling for real-time updates
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [merchantId]);

  const fetchNotifications = async () => {
    try {
      // This would typically fetch from your notifications API
      // For now, we'll simulate some notifications
      const mockNotifications = [
        {
          id: 1,
          type: 'success',
          title: 'PAN Verification Successful',
          message: 'Your PAN card has been successfully verified.',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
          read: false
        },
        {
          id: 2,
          type: 'info',
          title: 'Document Upload Required',
          message: 'Please upload your cancelled cheque to complete the bank verification.',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
          read: true
        },
        {
          id: 3,
          type: 'warning',
          title: 'GST Verification Pending',
          message: 'Your GST verification is taking longer than usual. Our team is working on it.',
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
          read: true
        }
      ];
      
      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const getNotificationIcon = (type) => {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type] || 'ℹ️';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes} minutes ago`;
    } else if (hours < 24) {
      return `${hours} hours ago`;
    } else {
      return `${days} days ago`;
    }
  };

  if (loading) {
    return (
      <div className="kyc-notifications-container">
        <div className="notifications-header">
          <h3>📢 KYC Updates</h3>
        </div>
        <div className="loading-notifications">Loading notifications...</div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="kyc-notifications-container">
      <div className="notifications-header">
        <h3>📢 KYC Updates</h3>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount} new</span>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="no-notifications">
          <p>No notifications yet</p>
          <small>You'll receive updates about your KYC progress here</small>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map(notification => (
            <div 
              key={notification.id}
              className={`notification-item ${notification.type} ${!notification.read ? 'unread' : ''}`}
              onClick={() => markAsRead(notification.id)}
            >
              <div className="notification-content">
                <div className="notification-header">
                  <span className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <span className="notification-title">
                    {notification.title}
                  </span>
                  <button 
                    className="dismiss-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notification.id);
                    }}
                  >
                    ×
                  </button>
                </div>
                <p className="notification-message">
                  {notification.message}
                </p>
                <div className="notification-footer">
                  <span className="notification-time">
                    {formatTimestamp(notification.timestamp)}
                  </span>
                  {!notification.read && (
                    <span className="unread-indicator">•</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KYCNotifications;